import { useEffect, useState } from 'react'
import { projectsApi, generationsApi } from '../lib/api'
import type { Project, Generation } from '../types'

interface MoveToProjectModalProps {
  isOpen: boolean
  onClose: () => void
  generation: Generation
  onMoved: (projectId: string | null, projectName: string | null) => void
}

export function MoveToProjectModal({
  isOpen,
  onClose,
  generation,
  onMoved,
}: MoveToProjectModalProps) {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    generation.projectId
  )

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      setSelectedProjectId(generation.projectId)
      projectsApi
        .getAll()
        .then(setProjects)
        .catch(console.error)
        .finally(() => setLoading(false))
    }
  }, [isOpen, generation.projectId])

  const handleSelect = async (projectId: string | null) => {
    if (projectId === generation.projectId) {
      onClose()
      return
    }

    setSaving(true)
    try {
      const result = await generationsApi.update(generation.id, { projectId })
      onMoved(result.projectId, result.projectName)
      onClose()
    } catch (error) {
      console.error('Failed to move to project:', error)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl shadow-hover z-50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-color">
          <h3 className="font-serif text-lg font-semibold text-text-main">
            Move to Project
          </h3>
          <button
            onClick={onClose}
            className="size-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-text-muted"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-2 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="py-8 flex justify-center">
              <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-1">
              {/* No Project Option */}
              <button
                onClick={() => handleSelect(null)}
                disabled={saving}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                  selectedProjectId === null
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-gray-50 text-text-main'
                }`}
              >
                <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-text-muted">
                    folder_off
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">No Project</p>
                  <p className="text-xs text-text-muted">Remove from current project</p>
                </div>
                {selectedProjectId === null && (
                  <span className="material-symbols-outlined text-primary">
                    check_circle
                  </span>
                )}
              </button>

              {/* Project List */}
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleSelect(project.id)}
                  disabled={saving}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                    selectedProjectId === project.id
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-gray-50 text-text-main'
                  }`}
                >
                  {/* Project Thumbnail */}
                  <div className="size-10 rounded-lg overflow-hidden flex-shrink-0">
                    {project.coverImage ? (
                      <img
                        src={project.coverImage}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                        <span className="material-symbols-outlined text-sm text-primary/60">
                          folder
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{project.name}</p>
                    <p className="text-xs text-text-muted">
                      {project.imageCount} images
                    </p>
                  </div>
                  {selectedProjectId === project.id && (
                    <span className="material-symbols-outlined text-primary">
                      check_circle
                    </span>
                  )}
                </button>
              ))}

              {projects.length === 0 && (
                <div className="py-6 text-center text-text-muted">
                  <span className="material-symbols-outlined text-3xl mb-2 block">
                    folder_open
                  </span>
                  <p className="text-sm">No projects yet</p>
                  <p className="text-xs mt-1">Create a project to organize your images</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Saving Overlay */}
        {saving && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
              <span className="text-sm text-text-muted">Moving...</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
